import { AgoraVideoPlayer, IAgoraRTCRemoteUser } from 'agora-rtc-react';
import { RtmMessage } from 'agora-rtm-sdk';
import React, {
  ReactElement,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { IoVolumeMediumOutline, IoVolumeMuteOutline } from 'react-icons/io5';
import {
  useEndViewLiveMutation,
  useGetLiveCompetitionsMutation,
  useGetLiveMatchMutation,
  useGetLiveSettingMutation,
  useSendLikeMutation,
} from '../../../../services/api/apiProvider';
import { LiveProps } from '../../LiveContainer';
import { Debug } from '../../../../common/utils/debugLogs';
import { MessageType } from '../../types/messageType';
import './LiveMediaPlayerAudience.scss';
import { LiveEventItem } from '../LiveEvents/ListEventItem';
import { convertDataToUID, randomUUID } from '../../../../common/utils/utils';
import { VoiceGuestModal } from '../VoiceGuest/VoiceGuestModal';
import { VoiceGuestBtn } from '../VoiceGuest/VoiceGuestBtn';
import { useDispatch, useSelector } from 'react-redux';
import { actions } from '../../liveSlice';
import handUp from '../../../../assets/svg/sankakibou.svg';
import handUpUser from '../../../../assets/svg/sankakibou2.svg';
import btnReturn from '../../../../assets/png/btn_str_return-1.png';
import { VoiceGuestNoticeModal } from '../VoiceGuest/VoiceGuestNotice';
import { ImageCustom } from '../../../../common/components/ImageCustom';
import {
  LiveCompetitionResult,
  ResponseLiveCompetitions,
  ResponseLiveMatch,
} from '../../../../services/api/models/response/responseLiveMatch';
import LiveMatch from '../../../live-match/components/LiveMatch/LiveMatch';
import {
  resetDataLiveMatch,
  toggleLiveMatchList,
  updateFinishLiveCompetition,
  updateLiveCompetitions,
  updateLiveMatchData,
  updateLiveMatchStatus,
  updateSendDateTime,
  updateUserLiveReturn,
} from '../../../live-match/liveMatchSlice';
import ButtonStrMatch from '../../../../assets/svg/btn_str_match.svg';
import ButtonStrMatchBan from '../../../../assets/svg/btn_str_match_ban.svg';
import LiveMatchListModal from '../../../live-match/components/LiveMatchListModal/LiveMatchListModal';
import { AppState, store } from '../../../../store/store';
import { LiveMatchStartEnd } from '../../../live-match/types/LiveMatchTypes';
import remoteUserImgNone from '../../../../assets/png/img_none.png';
import BtnHeart from '../../../../assets/svg/btn_str_hart.svg';
import { useThrottle } from '../../../../common/hooks/useThrottle';
import { reproio } from '../../../../common/utils/reproio';
import MobileDetect from 'mobile-detect';
import { BsFillMicMuteFill as MuteIcon } from 'react-icons/bs';
import { APP_BASE_URL } from '../../../../loadEnv';

export const LiveMediaPlayerAudience = (
  props: LiveProps & { clickLike: (anchor?: any) => void },
): ReactElement => {
  const userRef = useRef<IAgoraRTCRemoteUser | null>(null);
  const [, triggerRender] = useState('');
  const [retryLoginCount, setRetryLoginCount] = useState(0);
  const [endView] = useEndViewLiveMutation();
  const [getLiveSetting] = useGetLiveSettingMutation();
  // test get live match data
  const [getLiveMatch] = useGetLiveMatchMutation();
  const [getLiveCompetitions] = useGetLiveCompetitionsMutation();
  const liveMatchState = useSelector((state: AppState) => state.liveMatch);
  const [isMute, setMute] = useState(false);
  const [isShowMute, setIsShowMute] = useState(false);
  const [sendLike] = useSendLikeMutation();
  const [ratio, setRatio] = useState<number>();
  const cameraTrackRef = useRef<IAgoraRTCRemoteUser>();
  //check if target user quit live match
  const isTargetQuitRef = useRef(false);
  const timeOutRef = useRef<Record<any, any>>({});

  const dispatch = useDispatch();

  const throttleSendLike = useThrottle(sendLike, 10, 1000, [sendLike]); // throttle call API max 10 times in 1s

  const handleLike = () => {
    const userData = props.liveState.userData;
    throttleSendLike(props.liveState.liveData!.id);
    // show first comment like
    if (
      !props.liveState.liveComments.some(
        (cmt) =>
          cmt.commentConfig === MessageType.SEND_NICE &&
          cmt.commentUser?.id === userData?.id &&
          cmt.comment?.startsWith('いいね'),
      )
    )
      props.addLiveComment({
        commentConfig: MessageType.SEND_NICE,
        comment: 'いいね❤',
        commentUser: {
          loginId: userData?.loginId,
          level: userData?.level,
          id: userData?.id,
        },
      });
    //create heart animation
    props.clickLike('.live-media-player__likebox');
  };

  //track repro event
  useEffect(() => {
    reproio('track', 'screen audience');
    timeOutRef.current.reproioTrack10s = setTimeout(() => {
      reproio('track', 'screen audience 10s');
    }, 10000);
    timeOutRef.current.reproioTrack30s = setTimeout(() => {
      reproio('track', 'screen audience 30s');
    }, 30000);
    return () => {
      clearTimeout(timeOutRef.current.reproioTrack10s);
      clearTimeout(timeOutRef.current.reproioTrack30s);
      // set LastDayToWatch when move other page without reload
      reproio('setDateUserProfile', 'LastDayToWatch', new Date());
    };
  }, []);

  const toggleMute = () => {
    // handle mute
    userRef.current?.audioTrack?.setVolume(isMute ? 200 : 0);
    setMute(!isMute);
  };

  const toggleMirror = (enable: boolean) => {
    const video = document.querySelector(
      '.agora_video_player',
    ) as HTMLVideoElement;

    if (video) {
      video.style['transform'] = enable ? '' : 'rotateY(180deg)';
    }
  };

  const fetchLiveSetting = () => {
    if (props.liveState.liveData) {
      getLiveSetting(props.liveState.liveData.id)
        .unwrap()
        .then((setting) => {
          if (props.liveState.isLive) {
            toggleMirror(setting.mirror === 'ON');
          }
          setIsShowMute(setting.mute === 'ON');
        });
    }
  };

  const resizeVideo = () => {
    if (!ratio) {
      return;
    }
    const md = new MobileDetect(window.navigator.userAgent);
    if (md.os() === 'AndroidOS' || md.os() === 'iOS') {
      return;
    }

    const element = document.querySelector(
      '.live-media-player__content',
    ) as HTMLDivElement;
    if (!element) {
      return;
    }

    const newWidth = Math.round(element.clientHeight * ratio);
    element.style.width = liveMatchState.liveMatchData
      ? `${newWidth * 1.7}px`
      : `${newWidth}px`;

    dispatch(actions.toggleResizeStatus());
  };

  useEffect(() => {
    // not join or start live yet!
    if (!props.liveState.isInLive || props.liveState.liveData === undefined) {
      return;
    }
    // join rtc channel
    getLiveMatch(props.liveState.liveData?.id)
      .unwrap()
      .then((liveMatchData: ResponseLiveMatch) => {
        dispatch(updateLiveMatchData(liveMatchData));
        dispatch(updateLiveMatchStatus(LiveMatchStartEnd.START));
        props.liveState.rtcEngine
          ?.joinChannel(
            liveMatchData.id,
            null,
            'subscriber',
            liveMatchData.live.id,
          )
          .then(async () => {
            if (props.liveState.liveData?.id) {
              const response = await getLiveCompetitions(
                props.liveState.liveData.id,
              ).unwrap();
              if (response) {
                dispatch(updateLiveCompetitions(response));
              }
            }
          })
          .catch(() => {
            setTimeout(async () => {
              setRetryLoginCount(retryLoginCount + 1); // force rejoin channel
            }, 1000);
          });
      })
      .catch(() => {
        if (props.liveState.liveData) {
          props.liveState.rtcEngine
            ?.joinChannel(props.liveState.liveData?.id, null, 'subscriber')
            .catch(() => {
              setTimeout(async () => {
                setRetryLoginCount(retryLoginCount + 1); // force rejoin channel
              }, 1000);
            });
          props.liveState.rtcEngine?.removeAllListeners('user-published');
          props.liveState.rtcEngine?.removeAllListeners('user-unpublished');
          props.liveState.rtcEngine?.on('user-published', showRemoteUserStream);
          props.liveState.rtcEngine?.on(
            'user-unpublished',
            handleRemoteUserUnpublish,
          );
        }
      });

    // prevent duplicate event when fail to join channel
    // props.liveState.rtcEngine?.removeAllListeners('user-published');
    // props.liveState.rtcEngine?.on('user-published', showRemoteUserStream);

    listenChannelMessage();

    // clean up
    return () => {
      if (
        props.liveState.liveData?.id !== undefined &&
        !props.liveState.isEnded
      ) {
        dispatch(resetDataLiveMatch());
        endView(props.liveState.liveData?.id);
      }

      // remote remote user media tracks when stop
      userRef.current?.audioTrack?.stop();
      userRef.current?.videoTrack?.stop();
    };
  }, [props.liveState.isInLive, retryLoginCount]);

  const handleRemoteUserUnpublish = (
    unpublishUser: IAgoraRTCRemoteUser,
    mediaType: 'audio' | 'video',
  ) => {
    if (mediaType === 'video') {
      const hashUser = convertDataToUID(props.liveState.liveData?.user.id);
      if (hashUser !== unpublishUser.uid) {
        cameraTrackRef.current = undefined;
      } else {
        userRef.current = null;
      }
      triggerRender(randomUUID());
    }
  };

  const listenChannelMessage = () => {
    props.liveState.rtmEngine?.on(
      'ChannelMessage',
      (message: RtmMessage): void => {
        if (message.messageType === 'TEXT') {
          const content = JSON.parse(message.text);
          // Update senDateTime if join a live match
          if (content.type === MessageType.JOIN_USER) {
            const { liveMatchData, sendDateTime } = store.getState().liveMatch;
            if (liveMatchData && !sendDateTime) {
              dispatch(updateSendDateTime(content.sendDateTime));
            }
          }
          // Mirror On
          if (content.type === MessageType.MIRRORING) {
            Debug.log('MIRROR ON');
            toggleMirror(true);
          }

          // Mirror Off
          if (content.type === MessageType.UN_MIRRORING) {
            Debug.log('MIRROR OFF');
            toggleMirror(false);
          }
          // Start Mute
          if (content.type === MessageType.MUTE) {
            Debug.log('START MUTE');
            setIsShowMute(true);
          }
          // End Mute
          if (content.type === MessageType.UN_MUTE) {
            Debug.log('END MUTE');
            setIsShowMute(false);
          }

          // Liver end live
          if (content.type === MessageType.END_LIVE) {
            Debug.log('LIVE IS END');
            props.endLive();
          }
          // host accept pair
          if (content.type === MessageType.START_ACCEPTING_PAIR) {
            Debug.log('START ACCEPT PAIR');
            props.handleDisableButtonRequest(true);
          }
          // host not accept pair
          if (content.type === MessageType.END_ACCEPTING_PAIR) {
            Debug.log('END ACCEPT PAIR');
            props.handleDisableButtonRequest(false);
          }
          // hot start pair
          if (content.type === MessageType.START_PAIR) {
            Debug.log('START PAIR');
            const data = content.value.user;
            if (data.id === props.liveState.userData?.id) {
              props.toggleNoticeApprovePairModal(true);
            }
            dispatch(actions.updateUserPair(data));
          }
          // hot end pair
          if (content.type === MessageType.END_PAIR) {
            Debug.log('END PAIR');
            if (content.value.user.id === props.liveState.userData?.id) {
              props.handleChangeButtonRequest(false);
            }
            dispatch(actions.updateUserPair(undefined));
          }
          // guest leave
          if (content.type === MessageType.LEAVE_USER) {
            Debug.log('LEAVE LIVE');
            if (content.value.id === props.liveState.userData?.id) {
              props.handleChangeButtonRequest(false);
              props.toggleVoiceGuestModal(false);
              window.location.href = `${window.location.origin}/${APP_BASE_URL}`;
            }
          }
          if (content.type === MessageType.REQUEST_LIVE_MATCH) {
            dispatch(updateLiveMatchData(content.value.liveMatch));
          }
          if (content.type === MessageType.START_LIVE_MATCH) {
            dispatch(updateLiveMatchData(content.value.liveMatch));
            dispatch(updateSendDateTime(content.sendDateTime));
            dispatch(updateLiveMatchStatus(LiveMatchStartEnd.START));
            userRef.current = null;
            props.liveState.rtcEngine?.leaveChannel().then(() => {
              props.liveState.rtcEngine
                ?.joinChannel(
                  content.value.liveMatch.id,
                  null,
                  'subscriber',
                  content.value.liveMatch.live.id,
                )
                .catch(() => {
                  setTimeout(async () => {
                    setRetryLoginCount(retryLoginCount + 1); // force rejoin channel
                  }, 1000);
                });
            });
          }
          if (content.type === MessageType.REJECT_LIVE_MATCH) {
            dispatch(updateLiveMatchData(undefined));
          }
          if (content.type === MessageType.START_LIVE_COMPETITION) {
            dispatch(updateLiveCompetitions(content.value.liveCompetition));
          }
          if (content.type === MessageType.NOTIFY_LIVE_COMPETITION_SCORE) {
            dispatch(updateLiveCompetitions(content.value.liveCompetition));
          }
          if (content.type === MessageType.END_LIVE_MATCH) {
            setTimeout(
              () => {
                isTargetQuitRef.current = false;
                triggerRender(randomUUID());
                dispatch(resetDataLiveMatch());
                userRef.current = null;
                cameraTrackRef.current = undefined;
                props.liveState.rtcEngine?.leaveChannel().then(() => {
                  if (
                    !props.liveState.isInLive ||
                    props.liveState.liveData === undefined
                  ) {
                    return;
                  }
                  props.liveState.rtcEngine
                    ?.joinChannel(
                      props.liveState.liveData.id,
                      null,
                      'subscriber',
                    )
                    .catch(() => {
                      setTimeout(async () => {
                        setRetryLoginCount(retryLoginCount + 1); // force rejoin channel
                      }, 1000);
                    });
                });
                props.liveState.rtcEngine?.removeAllListeners('user-published');
                props.liveState.rtcEngine?.removeAllListeners(
                  'user-unpublished',
                );
                props.liveState.rtcEngine?.on(
                  'user-published',
                  showRemoteUserStream,
                );
                props.liveState.rtcEngine?.on(
                  'user-unpublished',
                  handleRemoteUserUnpublish,
                );
              },
              isTargetQuitRef.current ? 7000 : 0,
            );
          }
          if (content.type === MessageType.END_LIVE_COMPETITION) {
            const liveCompetition = content.value
              .liveCompetition as ResponseLiveCompetitions;
            const liveData = store.getState().live.liveData;
            const isTargetQuit = liveCompetition.scores?.some(
              (score) =>
                score.user.id !== liveData?.user.id &&
                score.result === LiveCompetitionResult.Abstention,
            );
            if (isTargetQuit) {
              isTargetQuitRef.current = isTargetQuit;
              triggerRender(randomUUID());
              dispatch(updateFinishLiveCompetition(true));
            }
            dispatch(updateLiveCompetitions(content.value.liveCompetition));
          }
          if (content.type === MessageType.NOTIFY_EVENT_RANKING) {
            dispatch(props.updateEventRankings(content.value.eventRankings));
          }
        }
      },
    );
  };

  // Ratio effect. Resize video
  useEffect(() => {
    resizeVideo();

    const onWindowResize = () => {
      resizeVideo();
    };
    window.addEventListener('resize', onWindowResize);

    return () => {
      window.removeEventListener('resize', onWindowResize);
    };
  }, [ratio]);

  useEffect(() => {
    if (liveMatchState.liveMatchData) {
      props.liveState.rtcEngine?.removeAllListeners('user-published');
      props.liveState.rtcEngine?.on('user-published', showRemoteUserStream);
    }
  }, [liveMatchState.liveMatchData]);

  // show remoteUser
  const showRemoteUserStream = async (
    remoteUser: IAgoraRTCRemoteUser,
    mediaType: 'audio' | 'video',
  ) => {
    Debug.log(`----> user-published: ${remoteUser.uid}`);
    await props.liveState.rtcEngine?.instance?.subscribe(remoteUser, mediaType);

    const hashUser = convertDataToUID(props.liveState.liveData?.user.id);

    if (!liveMatchState.liveMatchData) {
      if (mediaType === 'video') {
        userRef.current = remoteUser;
        triggerRender(randomUUID());
        fetchLiveSetting();

        // resize video player
        remoteUser.videoTrack?.on('first-frame-decoded', () => {
          const videoSize = remoteUser.videoTrack?.getCurrentFrameData();
          if (videoSize) {
            setRatio(videoSize.width / videoSize.height);
          }
        });
      }

      if (mediaType === 'audio') {
        remoteUser.audioTrack?.play();
      }
    } else {
      if (hashUser !== remoteUser.uid) {
        if (mediaType === 'video') {
          cameraTrackRef.current = remoteUser;
          triggerRender(randomUUID());
          fetchLiveSetting();

          // resize video player
          remoteUser.videoTrack?.on('first-frame-decoded', () => {
            const videoSize = remoteUser.videoTrack?.getCurrentFrameData();
            if (videoSize) {
              setRatio(videoSize.width / videoSize.height);
            }
          });
        }

        if (mediaType === 'audio') {
          remoteUser.audioTrack?.play();
        }
      } else {
        if (mediaType === 'video') {
          userRef.current = remoteUser;
          triggerRender(randomUUID());
          fetchLiveSetting();

          // resize video player
          remoteUser.videoTrack?.on('first-frame-decoded', () => {
            const videoSize = remoteUser.videoTrack?.getCurrentFrameData();
            if (videoSize) {
              setRatio(videoSize.width / videoSize.height);
            }
          });
        }

        if (mediaType === 'audio') {
          remoteUser.audioTrack?.play();
        }
      }
    }
  };

  const handleOpenListMatchModal = () => {
    dispatch(toggleLiveMatchList(true));
  };

  const handleClickLiveReturn = () => {
    dispatch(updateUserLiveReturn(false));
    history.back();
  };

  // not join live yet!
  if (!props.liveState.isInLive || props.liveState.liveData === undefined) {
    return <></>;
  }

  return (
    <>
      {userRef.current && userRef.current?.videoTrack && (
        <AgoraVideoPlayer
          className="live-media-player__video"
          videoTrack={userRef.current.videoTrack}
        />
      )}

      {isTargetQuitRef.current ? (
        <img
          src={remoteUserImgNone}
          className="live-media-player__content__none-img"
        />
      ) : (
        <>
          {cameraTrackRef.current && (
            <>
              {cameraTrackRef.current.videoTrack && (
                <AgoraVideoPlayer
                  className="live-media-player__video"
                  videoTrack={cameraTrackRef.current.videoTrack}
                />
              )}
            </>
          )}
        </>
      )}

      <LiveMatch />

      {isShowMute && (
        <div className={'live-media-player__mute-notify'}>
          <MuteIcon />
          ミュート中
        </div>
      )}

      {!props.liveState.isLive && (
        <VoiceGuestBtn audientGuest={props.liveState.userPair} />
      )}

      {/* Button control */}
      <div
        className={`live-media-player__control "  ${
          props.liveState.isLive ? '' : 'live-media-player__control--voice'
        }`}
      >
        <div
          className={
            props.liveState.isLive
              ? 'live-media-player__like'
              : `${
                  props.liveState.userData?.liverLeague === 0
                    ? 'live-media-player__like'
                    : 'live-media-player__like voice'
                }`
          }
        >
          <div
            className="live-media-player__likebox live-media-player__control__wrapper"
            style={{ display: 'flex' }}
          >
            <div className="live-media-player__like__icon__holder"></div>
            <img src={BtnHeart} onClick={handleLike} />
          </div>
        </div>

        {props.liveState.isLive ? (
          <></>
        ) : (
          <div className="live-media-player__control__wrapper">
            {props.liveState.userData?.liverLeague !== 0 && (
              <>
                {props.liveState.userData?.id ===
                props.liveState.userPair?.id ? (
                  <ImageCustom
                    onClick={() => props.toggleVoiceGuestModal(true)}
                    className="live-media-player__control__hand"
                    src={handUpUser}
                  />
                ) : (
                  <ImageCustom
                    onClick={() => props.toggleVoiceGuestModal(true)}
                    className="live-media-player__control__hand"
                    src={handUp}
                  />
                )}
              </>
            )}
          </div>
        )}
        <div className="live-media-player__control__wrapper">
          <div
            className="live-media-player__control__mute"
            onClick={toggleMute}
          >
            {isMute ? <IoVolumeMuteOutline /> : <IoVolumeMediumOutline />}
          </div>
        </div>
        {props.liveState.isEnableGift ? (
          <div
            onClick={handleOpenListMatchModal}
            className="live-media-player__control__wrapper"
          >
            <img src={ButtonStrMatch} alt="btn-str-match" />
          </div>
        ) : (
          <div
            className="live-media-player__control__wrapper"
            style={{ cursor: 'default' }}
          >
            <img src={ButtonStrMatchBan} alt="btn-str-match" />
          </div>
        )}
      </div>

      {/* Live Event */}
      <div className="live-event-banner__list">
        {props.liveState.isEnableGift &&
          props.liveState.liveData?.events
            ?.slice()
            ?.reverse()
            .map((item) => {
              const eventItemProps = {
                liveProps: props,
                event: item.event,
              };
              return (
                <LiveEventItem
                  key={item.event?.id}
                  {...eventItemProps}
                ></LiveEventItem>
              );
            })}
      </div>
      {/* return live btn */}
      {liveMatchState.liveMatchData &&
        liveMatchState.userLiveReturn &&
        liveMatchState.userLiveReturn.length > 1 && (
          <div
            onClick={handleClickLiveReturn}
            style={{ backgroundImage: `url(${btnReturn})` }}
            className="live-competition-return-live-btn"
          >
            <ImageCustom
              src={liveMatchState.uriLiveReturn}
              className="live-competition-return-live-user"
            />
          </div>
        )}

      {liveMatchState.isShowListMatch && <LiveMatchListModal />}
      <VoiceGuestModal {...props} />
      {props.liveState.isShowNoticePairModal && (
        <VoiceGuestNoticeModal {...props} />
      )}
    </>
  );
};
